/**
 * OnboardingTour — floating 12-step "Get Started" checklist that orients new
 * users to the app. **On-demand only:** it never auto-opens. Users summon it
 * via
 *   1. Landing hero CTA "Get Started" (next to "Download the app")
 *   2. Pre-login desktop nav pill "Get Started"
 *   3. Hamburger menu (first item, "Get Started")
 *   4. Chatbot suggestion pill "Show me how to Get Started"
 * All four dispatch a `dp:open-tour` window event which un-dismisses the
 * checklist. Steps route to Investment 101 → Demo → Dividend Calculator →
 * Research → Build a portfolio (+ 7 more inside the builder). Progress
 * survives across sessions via localStorage.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X, ArrowRight, Sparkles, BookOpen, PlayCircle, Calculator, Compass, PlusCircle, Download, RotateCcw } from "lucide-react";

const STORAGE_KEY = "dp_onboarding_state";

// Ordered per user brief. Every `hint` shows on mouse-over of the row.
// `match` (optional) is a predicate on the current location that auto-ticks
// the step — e.g. Build a Portfolio finishes when the user lands anywhere
// under /portfolio/, while "Run a backtest" needs the ?backtest=1 hash.
const STEPS = [
  { id: "investment-101",  path: "/how-it-works",          label: "Investment 101",        icon: BookOpen,     hint: "Learn the fundamentals — DRIP, YoC, dividend safety — in a 3-minute primer." },
  { id: "demo",             path: "/demo",                  label: "Watch the Demo",       icon: PlayCircle,   hint: "See DPP build and backtest a portfolio in a 30-second walkthrough." },
  { id: "calculator",       path: "/calculator",            label: "Dividend Calculator",  icon: Calculator,   hint: "Project income across 30 years with DRIP, taxes, and inflation dialled in." },
  { id: "research",         path: "/research",              label: "Research a Ticker",    icon: Compass,      hint: "Pull the snapshot, safety score, and payout history for any US dividend stock or ETF." },
  { id: "build-portfolio",  path: "/portfolio/new",         label: "Build a Portfolio",    icon: PlusCircle,   hint: "Pick tickers, drag weight sliders, run a 30-year backtest. This is the payoff step." },
  { id: "add-tickers",      path: "/portfolio/new",         label: "Add More Tickers",     icon: PlusCircle,   hint: "Type any US symbol in the ticker search. Add 2–8 for a diversified sleeve.", markOn: "search" },
  { id: "adjust-inputs",    path: "/portfolio/new",         label: "Name & Set Inputs",    icon: Sparkles,     hint: "Give your portfolio a name, set a starting amount + date range, toggle DRIP." },
  { id: "run-backtest",     path: "/portfolio/new?run=1",   label: "Run the Backtest",     icon: PlayCircle,   hint: "Click 'Run backtest' to see historical growth, dividends, and drawdowns." },
  { id: "income-target",    path: "/portfolio/new?ai=1",    label: "How Much To Invest?", icon: Calculator,    hint: "Ask the AI Income Target Planner to reverse-solve for your dream income." },
  { id: "ai-explanation",   path: "/portfolio/new?ai=1",    label: "Read the AI Explanation", icon: BookOpen, hint: "Let the AI narrate what the numbers actually mean in plain English." },
  { id: "rebalance",        path: "/portfolio/new?opt=1",   label: "Rebalance Portfolio",  icon: Compass,      hint: "Hit AI Optimize — tilt for yield, growth, or a balanced blend." },
  { id: "download-report",  path: "/portfolio/new?export=1", label: "Print or Download",   icon: Download,     hint: "Export a branded PDF or the CSV of every position + backtest year." },
];

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    // NEW: Tour is on-demand only — hidden by default until the user
    // explicitly summons it via the landing hero CTA, hamburger, pre-login
    // nav pill, or chatbot suggestion (all dispatch `dp:open-tour`). This
    // eliminates the bottom-right double-widget clash with the auto-opening
    // chatbot on first visit.
    if (!raw) return { completed: [], dismissed: true, minimized: false };
    return { completed: [], dismissed: true, minimized: false, ...JSON.parse(raw) };
  } catch { return { completed: [], dismissed: true, minimized: false }; }
}

function saveState(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (_) {}
}

export default function OnboardingTour() {
  const [state, setState] = useState(loadState);
  const loc = useLocation();
  const nav = useNavigate();

  // Auto-mark a step complete the moment the user lands on its route. Uses
  // startsWith for `/portfolio/new` so `/portfolio/new?intent=…` also ticks.
  useEffect(() => {
    const hit = STEPS.find((s) => loc.pathname === s.path || loc.pathname.startsWith(s.path + "?"));
    if (!hit) return;
    setState((prev) => {
      if (prev.completed.includes(hit.id)) return prev;
      const next = { ...prev, completed: [...prev.completed, hit.id] };
      saveState(next);
      return next;
    });
  }, [loc.pathname]);

  // Listen for a global "open tour" event so the hamburger menu, pre-login
  // nav pill, and chatbot suggestion can all re-summon the checklist even
  // after a visitor has dismissed or minimized it.
  useEffect(() => {
    const handler = () => {
      setState((prev) => {
        const next = { ...prev, dismissed: false, minimized: false };
        saveState(next);
        return next;
      });
    };
    window.addEventListener("dp:open-tour", handler);
    return () => window.removeEventListener("dp:open-tour", handler);
  }, []);

  // Reset tour: clears every completed tick + opens the checklist to step 1.
  // Triggered by (a) the in-panel "Restart" button and (b) a global
  // `dp:reset-tour` event so the chatbot / settings can offer the same reset
  // to a user who's already dismissed the tour.
  useEffect(() => {
    const handler = () => {
      const next = { completed: [], dismissed: false, minimized: false };
      setState(next); saveState(next);
    };
    window.addEventListener("dp:reset-tour", handler);
    return () => window.removeEventListener("dp:reset-tour", handler);
  }, []);

  const dismiss = () => {
    const next = { ...state, dismissed: true };
    setState(next); saveState(next);
  };
  const toggleMin = () => {
    const next = { ...state, minimized: !state.minimized };
    setState(next); saveState(next);
  };
  const reset = () => {
    const next = { completed: [], dismissed: false, minimized: false };
    setState(next); saveState(next);
  };

  if (state.dismissed) return null;

  const doneCount = state.completed.length;
  const total = STEPS.length;
  const pct = Math.round((doneCount / total) * 100);
  const allDone = doneCount === total;

  return (
    <div
      className="fixed bottom-4 right-4 z-40 w-[320px] max-w-[calc(100vw-2rem)]"
      data-testid="onboarding-tour"
    >
      <Card className="shadow-2xl border-primary/30 bg-card">
        <CardContent className="p-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="w-4 h-4 text-secondary shrink-0" />
              <div className="min-w-0">
                <div className="mono text-[10px] uppercase tracking-widest text-secondary">Get started</div>
                <div className="serif text-sm truncate">
                  {allDone ? "You're all set!" : `${doneCount} of ${total} steps done`}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {doneCount > 0 && (
                <button
                  type="button"
                  onClick={reset}
                  className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-primary"
                  title={allDone ? "Restart tour from step 1" : "Reset progress — start over from step 1"}
                  data-testid="onboarding-reset"
                  aria-label="Restart tour"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={toggleMin}
                className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title={state.minimized ? "Expand tour" : "Minimize tour"}
                data-testid="onboarding-minimize"
                aria-label={state.minimized ? "Expand" : "Minimize"}
              >
                <ArrowRight className={`w-3.5 h-3.5 transition-transform ${state.minimized ? "-rotate-90" : "rotate-90"}`} />
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-destructive"
                title="Skip tour (won't come back)"
                data-testid="onboarding-dismiss"
                aria-label="Skip tour"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-muted">
            <div
              className="h-full bg-gradient-to-r from-secondary to-primary transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="onboarding-progress"
            />
          </div>

          {/* Checklist */}
          {!state.minimized && (
            <div className="p-2">
              {STEPS.map((s, i) => {
                const done = state.completed.includes(s.id);
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => nav(s.path)}
                    title={s.hint}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors ${
                      done ? "opacity-60" : "hover:bg-muted"
                    }`}
                    data-testid={`onboarding-step-${s.id}`}
                  >
                    <span className={`w-6 h-6 rounded-full grid place-items-center shrink-0 ${
                      done ? "bg-secondary text-secondary-foreground" : "border border-border text-muted-foreground"
                    }`}>
                      {done ? <Check className="w-3.5 h-3.5" /> : <span className="mono text-[10px]">{i + 1}</span>}
                    </span>
                    <Icon className={`w-4 h-4 shrink-0 ${done ? "text-muted-foreground" : "text-primary"}`} />
                    <span className={`text-sm truncate flex-1 ${done ? "line-through text-muted-foreground" : "text-foreground"}`}>
                      {s.label}
                    </span>
                    {!done && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                  </button>
                );
              })}
              {allDone && (
                <div className="mt-2 px-3 py-3 text-center">
                  <div className="text-xs italic text-muted-foreground mb-2">Nice work exploring DPP — happy investing!</div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={reset}
                    data-testid="onboarding-restart-cta"
                    className="rounded-full"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                    Restart the tour
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Footer opt-out */}
          <div className="px-4 py-2 border-t border-border flex items-center justify-between">
            <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Tour · {pct}%</span>
            <button
              type="button"
              onClick={dismiss}
              className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-destructive transition-colors"
              data-testid="onboarding-skip-footer"
              title="Dismiss for good — the tour will not return"
            >
              Skip tour
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
